import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {PersistGate} from 'redux-persist/integration/react';
import parse, {
    HTMLReactParserOptions,
    Element
} from 'html-react-parser';

import {__} from '~/shared/src/utilities/messageCatalog';
import Application from '~/shared/src/model/Application';
import messageUtils from '~/shared/src/utilities/messageUtils';

import AlertMessage from '~/frontend/src/components/base/dialogs/AlertMessage';
import DevTools from '~/frontend/src/components/base/page/DevTools';
import LanguageButton from '~/frontend/src/components/base/page/PageMenu/LanguageButton';
import LoadingMaskPanel from '~/frontend/src/components/base/LoadingMaskPanel';
import LoginDialog from '~/frontend/src/components/base/dialogs/LoginDialog';
import PageMainMenu from '~/frontend/src/components/base/page/PageMainMenu';
import PageMenu from '~/frontend/src/components/base/page/PageMenu';
import PageTabPanel from '~/frontend/src/components/base/page/PageTabPanel';
import XhrErrorDialog from '~/frontend/src/components/base/dialogs/XhrErrorDialog';

import {
    StoreState,
    persistor,
    AppDispatch
} from '~/frontend/src/store';
import {getTextColorForBackgroundColor} from '~/frontend/src/utilities/renderHelpers';
import openPanel, {PanelPropsWithPanelType} from '~/frontend/src/actions/panelManager/openPanel';
import activatePanel from '~/frontend/src/actions/panelManager/activatePanel';
import activateOrOpenPanelByPath from '~/frontend/src/actions/panelManager/activateOrOpenPanelByPath';


const getMode = () => matchMedia( '(prefers-color-scheme: dark)' ).matches ? 'dark' : 'light';
const getMobile = () => matchMedia( 'screen and (max-width: 960px)' ).matches;


const Page = () => {
    const dispatch : AppDispatch                      = useDispatch(),
          loadingSession                              = useSelector( ( state : StoreState ) => state.login?.loadingSession ),
          user                                        = useSelector( ( state : StoreState ) => state.session?.user ),
          openPanels                                  = useSelector( ( state : StoreState ) => state.panelManager?.openPanels ),
          [ mode, setMode ]                           = useState<'dark' | 'light'>( getMode() ),
          [ mobile, setMobile ]                       = useState<boolean>( getMobile() ),
          [ title, setTitle ]                         = useState<string>( '' ),
          [ logoStyle, setLogoStyle ]                 = useState<string>( '' ),
          [ titleStyle, setTitleStyle ]               = useState<string>( '' ),
          [ initialized, setInitialized ]             = useState<boolean>( false ),
          loggedIn                                    = useMemo(
              () => {
                  return user ? true : false;
              },
              [
                  !!user
              ]
          ),
          applicationToShow                           = useMemo(
              () => {
                  return global.landingPageApp ?? ( user ? user.application?.key : undefined );
              },
              [
                  global.landingPageApp ?? '',
                  user
              ]
          ),
          application                                 = useMemo(
              () => {
                  let retVal : Application | undefined;

                  if ( user ) {
                      for ( const app of user.applications ) {
                          if ( app.key === applicationToShow ) {
                              retVal = app;
                              break
                          }
                      }
                  }

                  return retVal;
              },
              [
                  applicationToShow,
                  user
              ]
          ),
          panelConfigRequestParametersMessageResolver = useMemo(
              () => {
                  if ( !global.landingPageRequestParameters ) {
                      return undefined;
                  }
                  else {
                      return messageUtils.buildReturnFunction( { requestParameters: global.landingPageRequestParameters } );
                  }
              },
              [
                  global.landingPageRequestParameters
              ]
          ),
          resolveRequestParametersInPanelConfig       = useCallback(
              ( panelConfig : { [ param : string ] : any } ) => {
                  const retVal : { [ param : string ] : any } = {},
                        params                                = Object.keys( panelConfig ) as string[];

                  for ( const param of params ) {
                      if ( typeof panelConfig[ param ] === 'string' ) {
                          retVal[ param ] = panelConfigRequestParametersMessageResolver !== undefined ? panelConfigRequestParametersMessageResolver( panelConfig[ param ] ) : panelConfig[ param ];
                      }
                      else if ( Array.isArray( panelConfig[ param ] ) ) {
                          retVal[ param ] = [] as any[];

                          for ( let i = 0; i < panelConfig[ param ].length; i++ ) {
                              retVal[ param ].push( resolveRequestParametersInPanelConfig( { arrayItem: panelConfig[ param ][ i ] } )[ 'arrayItem' ] );
                          }
                      }
                      else if ( typeof panelConfig[ param ] === 'object' ) {
                          retVal[ param ] = resolveRequestParametersInPanelConfig( panelConfig[ param ] );
                      }
                      else {
                          retVal[ param ] = panelConfig[ param ];
                      }
                  }
                  return retVal;
              },
              [
                  panelConfigRequestParametersMessageResolver
              ]
          ),
          defaultContent                              = '' +
              '<div class="yn-page-header">\n' +
              '    ${applicationImage}\n' +
              '    <div class="yn-page-header-title-background-mask" />\n' +
              '    <div class="yn-page-header-title" style="${applicationTitleStyle}">\n' +
              '        <div class="yn-page-header-title-inner">\n' +
              '            <div class="yn-page-header-title-text">\n' +
              '                ${applicationTitle}\n' +
              '            </div>\n' +
              '        </div>\n' +
              '    </div>\n' +
              '    ${pageMenu}\n' +
              '</div>\n' +
              '${mainMenu}\n' +
              '${tabPanel}',
          options : HTMLReactParserOptions            = {
              htmlparser2: {
                  lowerCaseTags: false
              },
              replace( domNode ) {
                  const domNodeName = ( domNode as Element ).name;

                  if ( domNodeName === 'pagemenu' && user ) {
                      return <div className="inline yn-page-header-menu" style={{ fontSize: 'xx-small' }}>
                          <PageMenu/>
                      </div>;
                  }
                  else if ( domNodeName === 'templanguageselector' ) {
                      return <div className="inline yn-page-header-menu" style={{ fontSize: 'xx-small' }}>
                          <LanguageButton
                              className="ml-1"
                              temporary={true}
                          />
                      </div>;
                  }
                  else if ( domNodeName === 'mainmenu' ) {
                      return <PageMainMenu showAsBurger={mobile}/>;
                  }
                  else if ( domNodeName === 'tabpanel' || domNodeName === 'tabbar' || domNodeName === 'tabcontent' ) {
                      return <PageTabPanel
                          hideTabContent={domNodeName === 'tabbar'}
                          hideTabBar={domNodeName === 'tabcontent'}
                          hidePanelHeader={!mobile}
                      />;
                  }

                  return false;
              }
          };

    // add/remove listener for color scheme switch ( on mount only -> [] )
    useEffect(
        () => {
            const matchMediaChangeHandler = () => {
                      setMode( getMode() );
                  },
                  matchWidthChangeHandler = () => {
                      setMobile( getMobile() );
                  };

            matchMedia( '(prefers-color-scheme: dark)' ).addEventListener( 'change', matchMediaChangeHandler );
            matchMedia( 'screen and (max-width: 960px)' ).addEventListener( 'change', matchWidthChangeHandler );
            return () => {
                matchMedia( '(prefers-color-scheme: dark)' ).removeEventListener( 'change', matchMediaChangeHandler );
                matchMedia( 'screen and (max-width: 960px)' ).removeEventListener( 'change', matchWidthChangeHandler );
            }
        },
        []
    );

    // set application title and image, and open default panel(s) for application if
    useEffect(
        () => {
            if ( application ) {
                let animate                = false,
                    newTitle : string      = '',
                    newLogoStyle : string  = '',
                    newTitleStyle : string = '';

                const animateIn = () => {
                    setTitle( newTitle );
                    setTitleStyle( newTitleStyle );
                    setLogoStyle( newLogoStyle );
                };

                // application title and coloring
                if ( application?.title ) {
                    newTitle = __( application.title );
                }

                if ( application?.color ) {
                    newTitleStyle = `background-color: ${application.color}; color: ${getTextColorForBackgroundColor( application.color )};`;
                }

                // animate current out
                if ( title !== '' && title !== newTitle ) {
                    setTitleStyle( 'background-color: transparent; color: transparent;' );

                    animate = true;
                }

                // application image
                if ( !!application?.image || ( mode === 'dark' && application?.darkImage ) ) {
                    let image = application.image;

                    if ( mode === 'dark' && application.darkImage ) {
                        image = application.darkImage;
                    }

                    newLogoStyle = `background-image = url(${image});`;
                }

                // animate current out
                if ( JSON.stringify( logoStyle ) !== '{}' && JSON.stringify( logoStyle ) !== JSON.stringify( newLogoStyle ) ) {
                    setLogoStyle( `${logoStyle} opacity: 0;` );

                    animate = true;
                }

                // animate new in
                if ( animate ) {
                    window.setTimeout( animateIn, 500 );
                }
                else {
                    animateIn();
                }

                // change browser title to application one
                if ( application?.windowTitle ) {
                    document.title = __( application.windowTitle, undefined, false );
                }
                else {
                    document.title = APP_TITLE + ' - ' + APP_VERSION;
                }

                // change favicon to application one
                if ( !!application?.icon || ( mode === 'dark' && application?.darkIcon ) ) {
                    const links = document.getElementsByTagName( 'link' );

                    let iconHref = `${BASE_URL}/ico/${application.iconFileName}`;

                    if ( mode === 'dark' && application.darkIcon ) {
                        iconHref = `${BASE_URL}/ico/${application.darkIconFileName}`;
                    }

                    for ( let i = 0; i < links.length; i++ ) {
                        if ( links[ i ].rel === 'icon' ) {
                            const linkHref = links[ i ].attributes.getNamedItem( 'href' )?.nodeValue ?? '';

                            // original icon or other app icon
                            if ( !links[ i ].id || ( links[ i ].id === 'appIcon' && !linkHref.match( `^${iconHref}\?` ) ) ) {
                                const iconLink     = document.createElement( 'link' ),
                                      documentHead = document.head || document.getElementsByTagName( 'head' )[ 0 ];

                                documentHead.removeChild( links[ i ] );

                                iconLink.id = 'appIcon';
                                iconLink.rel = 'icon';
                                iconLink.type = 'image/x-icon';
                                iconLink.href = `${iconHref}?${String.getHash()}`;

                                documentHead.appendChild( iconLink );
                            }
                        }
                    }
                }

                if ( initialized ) {

                    // open direct link panel
                    if ( global.directLink ) {
                        dispatch( activateOrOpenPanelByPath( global.directLink ) );

                        delete global.directLink;
                    }

                    // open default panels
                    else if ( !openPanels.length ) {

                        // open default panel for landingPage
                        if ( global.landingPagePanelConfig && user ) {
                            let panelConfig = user.getPanelConfig( global.landingPagePanelConfig ?? 0 );

                            if ( panelConfig ) {
                                if ( global.landingPageRequestParameters ) {
                                    panelConfig = resolveRequestParametersInPanelConfig( panelConfig ) as PanelPropsWithPanelType;
                                }
                                dispatch( openPanel( panelConfig ) );
                            }
                        }

                        // open default panels for application if there is at least one
                        else if ( application.defaultPanelConfigs.length ) {
                            for ( const panelConfig of application.defaultPanelConfigs ) {
                                dispatch( openPanel( panelConfig ) );
                            }

                            dispatch( activatePanel( application.defaultPanelConfigActive ) );
                        }
                    }
                }
            }
        },
        [
            application,
            initialized,
            mode
        ]
    );

    useEffect(
        () => {
            if ( !loadingSession && loggedIn ) {
                if ( !initialized ) {
                    setInitialized( true );
                }
            }
            else if ( initialized ) {
                setInitialized( false );
            }
        },
        [
            loadingSession,
            loggedIn
        ]
    );

    return useMemo(
        () => {
            // show initialize app
            if ( loadingSession ) {
                return <div className="loader">
                    <div className="loading"/>
                    <div className="loading-text">Initialisiere Anwendung...</div>
                </div>;
            }

            // show login dialog
            else if ( !loggedIn ) {
                return <div className="yn-page-background-effect">
                    <div className="yn-page-background-blend-effect">
                        <div className="yn-page-header">
                            <div className="yn-page-header-logo yoonite-logo"/>
                            <div className="yn-page-header-title-background-mask"/>
                        </div>
                        <LoginDialog/>
                        <div className="yn-page-powered-by">powered by<br/><span className="fsz-logo"/></div>
                    </div>
                </div>;
            }

            // show app
            else {
                const content = ( global.landingPageContent ?? defaultContent )
                    .replaceAll( '${applicationTitle}', title )
                    .replaceAll( '${applicationTitleStyle}', titleStyle )
                    .replaceAll( '${applicationImage}', `<div class="yn-page-header-logo yoonite-logo" style="${logoStyle}" />` )
                    .replaceAll( '${pageMenu}', '<PageMenu\/>' )
                    .replaceAll( '${tempLanguageSelector}', '<TempLanguageSelector\/>' )
                    .replaceAll( '${mainMenu}', '<mainmenu\/>' )
                    .replaceAll( '${tabPanel}', '<tabpanel\/>' )
                    .replaceAll( '${tabBar}', '<tabbar\/>' )
                    .replaceAll( '${content}', '<tabcontent\/>' )
                    .replaceAll( /<([a-zA-Z]+)+([^>]*?)\/>/g, '<$1$2></$1>' ); // convert self-closing tags to normal form (as parse can't handle them)

                return <>
                    <LoadingMaskPanel forComponent="global" className="yn-root-loading-mask">
                        <PersistGate persistor={persistor}>
                            <XhrErrorDialog/>
                            <AlertMessage/>
                            {parse( content, options )}
                        </PersistGate>
                    </LoadingMaskPanel>
                    <DevTools/>
                </>;
            }
        },
        [
            loadingSession,
            loggedIn,
            global.landingPageContent,
            mobile,
            title,
            titleStyle,
            logoStyle
        ]
    );
};

export default Page;
